/**
 * A PDF shared into MyDeck from another app (mobile M7) — Gmail, Files, a
 * chat. The sheet names the file, reads it as a statement on one tap, and if
 * the PDF is locked asks for its password right there (`/api/statements/unlock`).
 *
 * The file is judged by its own first bytes (`%PDF-`), never by the type the
 * sending app claimed: two issuers send statements as octet-stream, and the
 * manifest offers MyDeck for those too. The server checks the same way.
 *
 * Uploading from the phone sends the statement to production; the owner
 * allowed that for new statements (plan Q8). The training PDFs stay out.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, KeyRound, Loader2 } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { SheetContent } from "@/components/ui/Sheet";
import { Button, Input } from "@/components/ui/primitives";
import { statementsApi, type UploadResult } from "@/lib/api/client";
import { mailbox } from "@/lib/inbox";
import { haptic } from "@/platform";
import type { SharedFile } from "@/platform/share";

/** Files shared in, waiting for the sheet (filled at boot by main.tsx). */
export const sharedFiles = mailbox<SharedFile>();

/** Whether these bytes are a PDF, by the format's own header. */
export function isPdf(bytes: Uint8Array): boolean {
  const head = String.fromCharCode(...bytes.subarray(0, 1024));
  return head.includes("%PDF-");
}

type Phase =
  | { kind: "ready" }
  | { kind: "busy" }
  | { kind: "locked"; reviewItemId: string; failed: boolean }
  | { kind: "done"; result: UploadResult }
  | { kind: "error"; message: string };

export function ShareInSheet() {
  const qc = useQueryClient();
  const [queue, setQueue] = React.useState<SharedFile[]>([]);
  const [phase, setPhase] = React.useState<Phase>({ kind: "ready" });
  const [password, setPassword] = React.useState("");
  const file = queue[0] ?? null;

  React.useEffect(() => sharedFiles.listen((f) => setQueue((q) => [...q, f])), []);

  const next = () => {
    setQueue((q) => q.slice(1));
    setPhase({ kind: "ready" });
    setPassword("");
  };

  const settle = (result: UploadResult) => {
    void qc.invalidateQueries({ queryKey: ["statements"] });
    void qc.invalidateQueries({ queryKey: ["review-queue"] });
    void qc.invalidateQueries({ queryKey: ["dashboard"] });
    if (result.review_type === "password_needed" && result.review_item_id) {
      setPhase({ kind: "locked", reviewItemId: result.review_item_id, failed: false });
      return;
    }
    haptic(result.ok ? "success" : "warning");
    setPhase({ kind: "done", result });
  };

  const read = async () => {
    if (!file) return;
    setPhase({ kind: "busy" });
    try {
      const f = await file.read();
      const bytes = new Uint8Array(await f.slice(0, 1024).arrayBuffer());
      if (!isPdf(bytes)) {
        setPhase({ kind: "error", message: "That isn't a PDF. MyDeck reads the statement PDF your bank sends." });
        return;
      }
      // The server checks bytes too, but its name check is gone only since M7:
      // give the file a .pdf name so an older server still takes it.
      const named = /\.pdf$/i.test(f.name) ? f : new File([f], `${f.name || "statement"}.pdf`, { type: "application/pdf" });
      settle(await statementsApi.upload(named));
    } catch (e) {
      setPhase({ kind: "error", message: (e as Error).message });
    }
  };

  const unlock = async (reviewItemId: string) => {
    setPhase({ kind: "busy" });
    try {
      const result = await statementsApi.unlock(reviewItemId, password);
      setPassword("");
      if (result.review_type === "password_needed") setPhase({ kind: "locked", reviewItemId, failed: true });
      else settle(result);
    } catch {
      setPhase({ kind: "locked", reviewItemId, failed: true });
    }
  };

  return (
    <Dialog.Root open={file !== null} onOpenChange={(o) => !o && next()}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <SheetContent className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(30rem,94vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto">
          {file && (
            <div className="space-y-5 px-5 pb-6 pt-3">
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-hairline bg-raised text-brass">
                  <FileText className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <Dialog.Title className="font-display text-lg font-semibold text-ink">Read this statement?</Dialog.Title>
                  <Dialog.Description className="truncate text-xs text-faint">{file.name}</Dialog.Description>
                </div>
              </div>

              {phase.kind === "ready" && (
                <Button variant="primary" className="w-full" onClick={() => void read()}>
                  Read it into MyDeck
                </Button>
              )}

              {phase.kind === "busy" && (
                <p className="flex items-center gap-2 text-sm text-muted">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Reading…
                </p>
              )}

              {phase.kind === "locked" && (
                <form
                  className="space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void unlock(phase.reviewItemId);
                  }}
                >
                  <p className="flex items-start gap-2 text-sm text-muted">
                    <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-brass" aria-hidden />
                    {phase.failed
                      ? "That password didn't open it. Try again."
                      : "This PDF is locked. Type its password; MyDeck saves it, encrypted, for next time."}
                  </p>
                  <Input
                    type="password"
                    autoComplete="off"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-label="PDF password"
                    className="h-12"
                    autoFocus
                  />
                  <Button type="submit" variant="primary" className="w-full" disabled={!password}>
                    Unlock and read
                  </Button>
                </form>
              )}

              {phase.kind === "done" && (
                <div className="space-y-3">
                  <p className="text-sm text-ink">{phase.result.message}</p>
                  {phase.result.needs_review && (
                    <p className="text-xs text-warn">Some details need checking — it's waiting in Statements.</p>
                  )}
                  <div className="flex gap-2">
                    {phase.result.card_id && (
                      <Button asChild variant="default" className="flex-1">
                        <Link to={`/cards/${phase.result.card_id}`} onClick={next}>
                          Open the card
                        </Link>
                      </Button>
                    )}
                    <Button variant="primary" className="flex-1" onClick={next}>
                      Done
                    </Button>
                  </div>
                </div>
              )}

              {phase.kind === "error" && (
                <div className="space-y-3">
                  <p className="text-sm text-overdue">{phase.message}</p>
                  <Button variant="default" className="w-full" onClick={next}>
                    Close
                  </Button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
